import React from 'react';
import { BookmarkIcon, Trash2, Calendar } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatDate } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для сохраненных расчетов
 */
const SavedCalculationsSidebar = ({ 
  savedCalculations, 
  deleteSavedCalculation, 
  loadSavedCalculation 
}) => {
  // Если нет сохраненных расчетов
  if (!savedCalculations || savedCalculations.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BookmarkIcon className="w-5 h-5 mr-2 text-cyan-500" />
          Сохраненные расчеты
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные расчеты. Нажмите "Сохранить расчет" после выполнения вычислений.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BookmarkIcon className="w-5 h-5 mr-2 text-cyan-500" />
          Сохраненные расчеты
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedCalculations.length} {savedCalculations.length === 1 ? 'расчет' : savedCalculations.length < 5 ? 'расчета' : 'расчетов'}
        </span>
      </div>

      <div className="space-y-4 max-h-[400px] overflow-y-auto pr-1">
        {savedCalculations.map((calc) => (
          <div 
            key={calc.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {calc.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="xs"
                  variant="ghost"
                  icon={<Calendar className="w-4 h-4" />}
                  onClick={() => loadSavedCalculation(calc.id)}
                  title="Загрузить расчет"
                />
                <Button
                  size="xs"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedCalculation(calc.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {/* Отображение в зависимости от типа расчета */}
              {calc.formData.calculationType === 'difference' && (
                <>
                  <div className="flex justify-between mb-1">
                    <span>Период:</span>
                    <span className="font-medium">
                      {formatDate(calc.formData.startDate, 'short')} - {formatDate(calc.formData.endDate, 'short')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Результат:</span>
                    <span className="font-medium text-cyan-600 dark:text-cyan-400">
                      {calc.results.totalDays} дн.
                    </span>
                  </div>
                </>
              )}

              {calc.formData.calculationType === 'add' && (
                <>
                  <div className="flex justify-between mb-1">
                    <span>Добавлено:</span>
                    <span className="font-medium">
                      {calc.formData.daysToAddSubtract} дн. к {formatDate(calc.formData.startDate, 'short')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Результат:</span>
                    <span className="font-medium text-cyan-600 dark:text-cyan-400">
                      {formatDate(calc.results.resultDate, 'short')}
                    </span>
                  </div>
                </>
              )}

              {calc.formData.calculationType === 'subtract' && (
                <>
                  <div className="flex justify-between mb-1">
                    <span>Вычтено:</span>
                    <span className="font-medium">
                      {calc.formData.daysToAddSubtract} дн. из {formatDate(calc.formData.startDate, 'short')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Результат:</span>
                    <span className="font-medium text-cyan-600 dark:text-cyan-400">
                      {formatDate(calc.results.resultDate, 'short')}
                    </span>
                  </div>
                </>
              )}
              
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                Сохранено: {new Date(calc.date).toLocaleDateString('ru-RU')}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SavedCalculationsSidebar;